import { emptyAccountBalance, FlowTrackerSteps, REVIEW_FAQ } from 'common/constants';
import Accordion from 'components/common/Accordion/Accordion';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import ApproximateModal from 'components/common/modals/ApproximateModal';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectManualExternalAccount,
  selectManualSelectedHoldings,
  selectSelectedExternalAccount,
  selectSelectedHoldings,
  selectSelectedInternalAccount,
  selectTransferFlow,
  selectTransferType,
} from 'src/features/transfer/transferSlice';
import { RootState } from 'src/state/store';
import { getAccountTypeDisplayName } from 'utils/accountTypeUtils';
import {
  AccountType,
  AccountTypeDisplayNames,
  HoldingTransferType,
  TransferFlow,
  TransferTypeValue,
} from 'utils/constants';
import { calculateBalanceFromHoldings, formatCurrency } from 'utils/numberUtils';
import './ReviewTransfer.scss';

const ReviewTransfer: React.FC<object> = () => {
  const isManualFlow = useSelector(selectTransferFlow) === TransferFlow.Manual;
  const automaticExternalAccountDetails = useSelector(selectSelectedExternalAccount);
  const manualAccountDetails = useSelector(selectManualExternalAccount);
  const internalAccountDetails = useSelector(selectSelectedInternalAccount);
  const externalAccountIncomplete = useSelector(
    (state: RootState) => state.transfer.externalAccountIncomplete,
  );

  const externalAccountDetails = isManualFlow
    ? manualAccountDetails
    : automaticExternalAccountDetails;

  const accountTypeDisplayName = (() => {
    if (isManualFlow) {
      return AccountTypeDisplayNames[manualAccountDetails.accountType as AccountType];
    }
    if (automaticExternalAccountDetails?.missingFields?.includes('accountType')) {
      return getAccountTypeDisplayName(externalAccountIncomplete?.accountType);
    }
    return getAccountTypeDisplayName(
      automaticExternalAccountDetails?.selectedAccountType,
    );
  })();

  const institutionName = externalAccountDetails?.institutionName;
  const accountNumber =
    externalAccountDetails?.accountNumber ?? externalAccountIncomplete?.accountNumber;
  const internalAccountNumber = internalAccountDetails?.accountNumber;
  const holderName =
    internalAccountDetails?.accountHolderName ?? externalAccountIncomplete?.fullName;
  const internalAccountTypeDisplayName =
    internalAccountDetails?.selectedAccountType ?? 'None';
  const transferType = useSelector(selectTransferType);
  const isPartialTransfer = transferType === TransferTypeValue.PartialTransfer;
  const transferText = isPartialTransfer ? HoldingTransferType.PARTIAL : 'Entire account';

  const manualSelectedHoldings = useSelector(selectManualSelectedHoldings);
  const filteredManualHoldings =
    manualSelectedHoldings?.filter((holding) => holding.restored) ?? [];
  const selectedHoldings = useSelector(
    isManualFlow ? () => filteredManualHoldings : selectSelectedHoldings,
  );
  const investments = selectedHoldings
    ? selectedHoldings.map((holding) => `${holding.symbol}-${holding.quantity}`)
    : [];
  const balanceAmount = calculateBalanceFromHoldings(
    selectedHoldings,
    isManualFlow,
    isPartialTransfer,
    externalAccountDetails?.balance &&
      'currency' in externalAccountDetails.balance &&
      'amount' in externalAccountDetails.balance
      ? externalAccountDetails.balance
      : emptyAccountBalance,
  );
  const accountBalanceAmount = formatCurrency(
    balanceAmount?.amount,
    balanceAmount?.currency,
  );

  return (
    <>
      <ApproximateModal opensBy="#open-what-is-this-approximate-modal-button"></ApproximateModal>
      <div className="content-container b-grid --b-grid-gap=200">
        <div className="content-left b-grid-item b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
          <div className="b-grid">
            <span className="review-transfer-summary-header-container b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <label className="review-transfer-summary-header b-typography-sans-serif--headline--200 b-font-weight-bold">
                Transfer summary
              </label>
            </span>
            <div className="review-transfer-assets-card b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <span className="review-transfer-assets-card-header">
                <label className="b-typography-serif--headline--100 b-font-weight-bold">
                  Transferring assets from
                </label>
              </span>
              <div className="review-transfer-assets-card-row">
                <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                  Financial firm
                </span>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {institutionName}
                </span>
              </div>
              <div className="review-transfer-assets-card-row">
                <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                  Account type
                </span>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {accountTypeDisplayName}
                </span>
              </div>
              <div className="review-transfer-assets-card-row">
                <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                  Account number
                </span>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {accountNumber}
                </span>
              </div>
              <div className="review-transfer-assets-card-row">
                <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                  Transfer type
                </span>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {transferText}
                </span>
              </div>
              <div className="review-transfer-assets-card-row">
                <div className="review-assets-transfer-card-approximate-amount">
                  <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                    Approximate amount
                  </span>
                  <div
                    className="open-what-is-this-approximate-modal-button"
                    id="open-what-is-this-approximate-modal-button"
                  >
                    What is this?
                  </div>
                </div>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {accountBalanceAmount}
                </span>
              </div>
              {isPartialTransfer && (
                <div className="review-transfer-assets-card-row">
                  <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                    Investment(s)
                  </span>
                  <div className="review-transfer-assets-card-investments-list">
                    {investments.map((investment, index) => (
                      <div
                        key={index}
                        className="b-typography-sans-serif--body--200 b-font-weight-bold"
                      >
                        {investment}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="review-transfer-assets-to-card b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
              <span className="review-transfer-assets-to-card-header">
                <label className="b-typography-serif--headline--100 b-font-weight-bold">
                  Transferring assets to T. Rowe Price
                </label>
              </span>
              <div className="review-transfer-assets-to-card-row">
                <span className="b-typography-sans-serif--body--200 b-font-weight-light">
                  Account type
                </span>
                <span className="b-typography-sans-serif--body--200 b-font-weight-bold">
                  {`${holderName} — ${internalAccountTypeDisplayName} — ${internalAccountNumber}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
          <div className="flow-tracker-container">
            <FlowTracker currentFlowStep={4} items={FlowTrackerSteps} />
          </div>
          <div className="faq-container">
            <Accordion items={REVIEW_FAQ} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewTransfer;
